import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAdminApi } from '@/hooks/useAdminApi';
import {
  AlertCircle,
  BarChart3,
  CheckCircle,
  Clock,
  ExternalLink,
  Filter,
  Loader2,
  Medal,
  Search,
  Trophy,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

interface Score {
  id: string;
  user: {
    id: string;
    name: string | null;
    email: string | null;
  };
  workout: {
    id: string;
    name: string | null;
    scoring_type: string | null;
    scoring_direction: string | null;
  };
  affiliate: {
    id: string;
    name: string | null;
  } | null;
  value: number;
  notes: string | null;
  is_verified: boolean;
  video_url: string | null;
  created_at: string;
  updated_at: string;
}

export default function AdminScores() {
  const adminApi = useAdminApi();

  // Data state
  const [scores, setScores] = useState<Score[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [verificationFilter, setVerificationFilter] = useState<string>('');

  // Fetch scores
  const fetchScores = useCallback(
    async (cursor?: string) => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await adminApi.getScores({
          is_verified: verificationFilter === '' ? undefined : verificationFilter === 'verified',
          cursor,
          limit: 50,
        });

        if (response.success && response.data) {
          const data = response.data;
          if (cursor) {
            setScores((prev) => [...prev, ...data.scores]);
          } else {
            setScores(data.scores);
          }
          setHasMore(data.pagination.has_more);
          setNextCursor(data.pagination.next_cursor);
        } else {
          setError(response.error || 'Failed to load scores');
        }
      } catch (err) {
        console.error('Failed to fetch scores:', err);
        setError('Failed to load scores');
        toast.error('Failed to load scores');
      } finally {
        setIsLoading(false);
      }
    },
    [adminApi, verificationFilter]
  );

  useEffect(() => {
    fetchScores();
  }, [fetchScores]);

  // Format score value based on scoring type
  const formatScoreValue = (score: Score): string => {
    const value = score.value;
    const scoringType = score.workout.scoring_type;

    if (scoringType === 'time') {
      // Assume value is in seconds
      const minutes = Math.floor(value / 60);
      const seconds = value % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    if (scoringType === 'rounds') {
      return `${value} rounds`;
    }

    if (scoringType === 'reps') {
      return `${value} reps`;
    }

    if (scoringType === 'calories') {
      return `${value} cal`;
    }

    if (scoringType === 'distance') {
      return `${value}m`;
    }

    return value.toString();
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle verify/unverify
  const handleVerify = async (score: Score) => {
    try {
      const response = await adminApi.verifyScore(score.id, !score.is_verified);
      if (response.success) {
        toast.success(score.is_verified ? 'Score unverified' : 'Score verified');
        // Update local state
        setScores((prev) =>
          prev.map((s) => (s.id === score.id ? { ...s, is_verified: !s.is_verified } : s))
        );
      } else {
        toast.error(response.error || 'Failed to update verification');
      }
    } catch (err) {
      console.error('Verify error:', err);
      toast.error('Failed to update verification');
    }
  };

  // Filter scores by search
  const filteredScores = scores.filter((s) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      s.user.name?.toLowerCase().includes(query) ||
      s.user.email?.toLowerCase().includes(query) ||
      s.workout.name?.toLowerCase().includes(query) ||
      s.affiliate?.name?.toLowerCase().includes(query)
    );
  });

  // Calculate stats
  const totalScores = scores.length;
  const verifiedCount = scores.filter((s) => s.is_verified).length;
  const pendingCount = scores.filter((s) => !s.is_verified).length;

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gradient-gold">Platform Scores</h1>
        <p className="mt-1 text-muted-foreground">
          View and verify workout scores across all affiliates.
        </p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3 mb-8">
        <Card className="animate-fade-in">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-primary/10 p-3">
                <BarChart3 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Scores</p>
                <p className="text-2xl font-bold">{isLoading ? '...' : totalScores}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="animate-fade-in" style={{ animationDelay: '50ms' }}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-green-500/10 p-3">
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Verified</p>
                <p className="text-2xl font-bold">{isLoading ? '...' : verifiedCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="animate-fade-in" style={{ animationDelay: '100ms' }}>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="rounded-lg bg-orange-500/10 p-3">
                <Clock className="h-6 w-6 text-orange-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="text-2xl font-bold">{isLoading ? '...' : pendingCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by user, workout, or affiliate..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select
                options={[
                  { value: '', label: 'All Scores' },
                  { value: 'verified', label: 'Verified Only' },
                  { value: 'pending', label: 'Pending Only' },
                ]}
                value={verificationFilter}
                onChange={(e) => setVerificationFilter(e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Card className="mb-6 border-destructive">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <span>{error}</span>
              <Button variant="outline" size="sm" className="ml-auto" onClick={() => fetchScores()}>
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Scores Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            All Scores
            <Badge variant="secondary" className="ml-2">
              {filteredScores.length} scores
            </Badge>
          </CardTitle>
          <CardDescription>
            Workout scores submitted by members across all affiliates
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && scores.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : filteredScores.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <Medal className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-1">No scores found</h3>
              <p className="text-muted-foreground max-w-md">
                {searchQuery || verificationFilter
                  ? 'Try adjusting your search or filter criteria.'
                  : 'No workout scores have been logged yet.'}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Workout</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Affiliate</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredScores.map((score, index) => (
                    <TableRow
                      key={score.id}
                      className="animate-fade-in"
                      style={{ animationDelay: `${index * 30}ms` }}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium">{score.user.name || 'Unknown'}</p>
                          <p className="text-sm text-muted-foreground">{score.user.email || '-'}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{score.workout.name || 'Unknown'}</p>
                          <Badge variant="outline" className="text-xs">
                            {score.workout.scoring_type || 'Unknown'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono font-bold text-lg">
                          {formatScoreValue(score)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {score.affiliate?.name || (
                          <span className="text-muted-foreground">Individual</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            score.is_verified
                              ? 'bg-green-500/10 text-green-500 border-green-500/20'
                              : 'bg-orange-500/10 text-orange-500 border-orange-500/20'
                          }
                        >
                          {score.is_verified ? 'Verified' : 'Pending'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(score.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {score.video_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                score.video_url && window.open(score.video_url, '_blank')
                              }
                              title="View proof video"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleVerify(score)}
                            className={
                              score.is_verified
                                ? 'text-orange-500 hover:text-orange-600 hover:bg-orange-500/10'
                                : 'text-green-500 hover:text-green-600 hover:bg-green-500/10'
                            }
                          >
                            {score.is_verified ? (
                              <>
                                <XCircle className="h-3 w-3 mr-1" />
                                Unverify
                              </>
                            ) : (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Verify
                              </>
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Load More */}
              {hasMore && (
                <div className="flex justify-center mt-4">
                  <Button
                    variant="outline"
                    onClick={() => fetchScores(nextCursor || undefined)}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
